import { Button, Icon, Modal, Stack, Title } from "@introist/react-foundation/v2";
import { useMemo, useState } from "react";
import { api } from "../../../../services/rpc/RpcProvider";
import { SearchInput } from "../../../../components/molecules";
import { ListView } from "../../../../foundation";
import { ListRow } from "../../../../components/organisms";

export const SharepointFileSelector = ({
  accountId,
  value,
  onSelect
}: {
  accountId?: string;
  value?: string;
  onSelect: (identifier: string) => unknown;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(undefined);
  const [selectedDriveId, setSelectedDriveId] = useState<string | undefined>(undefined);
  const [selectedFolderPath, setSelectedFolderPath] = useState<string | undefined>(undefined);

  const { data: sites } = api.connectedAccounts.sharepoint.sites.useQuery(
    { accountId: accountId ?? "" },
    { enabled: !!accountId }
  );
  const { data: drives } = api.connectedAccounts.sharepoint.drives.useQuery(
    { accountId: accountId ?? "", siteId: selectedSiteId ?? "" },
    { enabled: !!accountId && !!selectedSiteId }
  );
  const { data: items } = api.connectedAccounts.sharepoint.items.useQuery(
    {
      accountId: accountId ?? "",
      siteId: selectedSiteId ?? "",
      driveId: selectedDriveId ?? "",
      folderPath: selectedFolderPath
    },
    {
      enabled: !!accountId && !!selectedSiteId && !!selectedDriveId
    }
  );

  const title = useMemo(() => {
    if (!value) return undefined;
    const titleParts = value.split("|");
    return titleParts.length > 1 ? titleParts[1] : undefined;
  }, [value]);

  const listData = useMemo(() => {
    if (selectedDriveId)
      return items?.map((f: any) => ({ ...f, type: f.isFolder ? "folder" : "file" }));
    if (selectedSiteId) return drives?.map(d => ({ ...d, type: "drive" }));

    return sites?.map(s => ({ ...s, type: "site" }));
  }, [selectedSiteId, selectedDriveId, sites, drives, items]);

  const filteredData = useMemo(() => {
    if (!search) return listData;
    return listData?.filter((item: any) => {
      const name = item.name.toLowerCase();
      return name.includes(search.toLowerCase());
    });
  }, [listData, search]);

  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        {title ?? "Select a file from Sharepoint"}
      </Button>
      <Modal title="Select a file from Sharepoint" open={open} onClose={() => setOpen(false)}>
        <Stack vertical style={{ width: "500px", height: "50vh" }}>
          <Stack>
            <Button
              variant="blended"
              startIcon="arrowLeft"
              onClick={() => {
                setSearch("");
                if (selectedFolderPath) {
                  const parts = selectedFolderPath.split("/");
                  parts.pop();
                  const path = parts.join("/");
                  if (path.endsWith(":")) setSelectedFolderPath(undefined);
                  else setSelectedFolderPath(path);
                } else if (selectedDriveId) {
                  setSelectedDriveId(undefined);
                } else if (selectedSiteId) {
                  setSelectedSiteId(undefined);
                } else {
                  setOpen(false);
                }
              }}
              style={{ marginRight: "auto" }}
            >
              Back
            </Button>
            <SearchInput style={{ flex: 1 }} value={search} onChange={setSearch} />
          </Stack>
          <ListView.List
            data={filteredData}
            style={{ overflowY: "auto" }}
            renderRow={(item: any) => (
              <ListRow
                gridTemplateColumns="auto 1fr auto"
                onClick={() => {
                  setSearch("");
                  if (item.type === "site") return setSelectedSiteId(item.id);
                  if (item.type === "drive") return setSelectedDriveId(item.id);
                  if (item.type === "folder") {
                    return setSelectedFolderPath(item.path);
                  }

                  onSelect(`${(item as any).driveId}:${item.id}|${item.name}`);
                  setOpen(false);
                }}
              >
                {<Icon name={item.type === "file" ? "file" : "folder"} />}
                <Title variant="bold">{item.name}</Title>
                {item.type !== "file" && <Icon name="chevronRight" />}
              </ListRow>
            )}
          />
        </Stack>
      </Modal>
    </>
  );
};
